<script setup lang="ts">
  import {
    h,
    ref,
    onMounted,
    onUnmounted,
    getCurrentInstance,
    watch,
    computed,
    VNode,
  } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import type {
    SelectRenderOption,
    SelectRenderLabel,
    SelectRenderTag,
    NotificationType,
  } from 'naive-ui'
  import {
    NImage,
    NPopover,
    NPopselect,
    useNotification,
    NButton,
    NTooltip,
  } from 'naive-ui'
  import { storeToRefs } from 'pinia'
  import { useI18n } from 'vue-i18n'
  import { emitter } from '@/common/mitt'
  import { formatTime, isEmpty, showAlert, showMessage } from '@/common/utils'
  import { useWebSocket } from '@/common/useWebSocket'
  import { deviceDetector } from '@/common/deviceDetector'
  import { apiWsHandshake } from '@/api/user'
  import { apiTaskStatus } from '@/api/task'
  import { useSiteStore } from '@/store/modules/useSiteStore'
  import { useConstantStore } from '@/store/modules/useConstantStore'
  import { useUserStore } from '@/store/modules/useUserStore'
  import { useAiCreateStore } from '@/store/modules/useAiCreateStore'
  import { useChatStore } from '@/store/modules/useChatStore'
  import LoginDialog from '@/components/common/LoginDialog.vue'
  import iconLogoPhotoup from '@/assets/logo/photoup-1.png'
  import iconLogoRemovebg from '@/assets/logo/removebg-1.png'
  import imgLogoPhotoup from '@/assets/logo/photoup-2.webp'
  import imgLogoRemovebg from '@/assets/logo/removebg-2.webp'

  const { VITE_APP_NAME } = import.meta.env,
    { isMobile } = deviceDetector(),
    router = useRouter(),
    route = useRoute(),
    { t, locale } = useI18n(),
    { proxy } = getCurrentInstance(),
    siteStore = useSiteStore(),
    {
      navItems,
      curNavIndex,
      theme,
      locale: storeLocale,
      supportedLocales,
    } = storeToRefs(siteStore),
    { setCurNavIndex, toggleTheme, setLocale } = siteStore,
    currentLocale = ref(storeLocale.value),
    constantStore = useConstantStore(),
    { TaskTypeList } = storeToRefs(constantStore),
    userStore = useUserStore(),
    { fastLogin, refreshPoints } = userStore,
    { isLoggedIn, isReady, getToken } = storeToRefs(userStore),
    aiCreateStore = useAiCreateStore(),
    {
      selectedTaskType,
      selectedNumberIndex,
      runningTaskId,
      runningTaskType,
      generatedGroupList,
      generateState,
    } = storeToRefs(aiCreateStore),
    {
      setRunningTaskId,
      setRunningTaskMessage,
      setGenerateState,
      addToGeneratedGroupList,
      clearLeftMenu,
    } = aiCreateStore,
    chatStore = useChatStore(),
    { roomId, chat } = storeToRefs(chatStore),
    { setRoomId } = chatStore,
    { VITE_WS_BASE_URL } = import.meta.env,
    notification = useNotification()

  let loginDialog = ref<InstanceType<typeof LoginDialog>>(),
    wsClose = null,
    wsSend = null,
    intervalId = null,
    isShowMobileNav = ref(false),
    isShowPopoverUI = ref(false),
    canHidePopoverUI = false

  let imgLogo = computed(() => {
      let logo
      switch (VITE_APP_NAME) {
        case 'removebg.pro':
          logo = imgLogoRemovebg
          break
        default:
          logo = imgLogoPhotoup
          break
      }
      return logo
    }),
    iconLogo = computed(() => {
      let logo
      switch (VITE_APP_NAME) {
        case 'removebg.pro':
          logo = iconLogoRemovebg
          break
        default:
          logo = iconLogoPhotoup
          break
      }
      return logo
    }),
    isChrome = computed(() => {
      const ua = navigator.userAgent
      return /Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)
    })

  onMounted(() => {
    watch(
      isReady,
      (ready) => {
        if (ready && isLoggedIn.value) {
          openSocket()
          loopTaskStatus()
        }
      },
      { immediate: true }
    )
    emitter.on('showLoginDialog', () => {
      loginDialog.value?.show()
    })
    emitter.on('loginSuccess', () => {
      openSocket()
      loopTaskStatus()
    })
    emitter.on('logoutSuccess', () => {
      if (typeof wsClose !== 'function') {
        return
      }
      wsClose()
    })
    emitter.on('joinChatRoom', (room_id) => {
      wsJoinRoom(room_id)
    })
    window.addEventListener('message', handleNewWindowMessage)
  })
  onUnmounted(() => {
    emitter.off('showLoginDialog')
    emitter.off('loginSuccess')
    emitter.off('logoutSuccess')
    emitter.off('joinChatRoom')
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    window.removeEventListener('message', handleNewWindowMessage)
  })
  watch(
    () => route.fullPath,
    (new_fullpath, old_fullpath) => {
      updateHeaderNav(new_fullpath)
    },
    { deep: true }
  )
  const handleNewWindowMessage = (event) => {
    if (event.data.type === 'LoginSuccess') {
      showMessage('Login success', 'success')
      location.reload()
    }
  }
  const updateHeaderNav = (cur_fullpath) => {
    let found = false
    for (let i = 0, len = navItems.value.length; i < len; i++) {
      if (cur_fullpath.includes(navItems.value[i].path)) {
        setCurNavIndex(i)
        found = true
        break
      }
    }
    if (!found) {
      setCurNavIndex(-1)
    }
  }
  const changeLanguage = () => {
    setLocale(currentLocale.value)
    locale.value = currentLocale.value
  }
  const onCredits = () => {
    if (route.name === 'pricing') {
      showMessage('You are already on the target page. ', 'info')
      return
    }
    hideUiPopover()
    router.push('/pricing')
  }
  const onLogin = () => {
    loginDialog.value?.show()
  }
  const showUiPopover = () => {
    canHidePopoverUI = false
    isShowPopoverUI.value = true
  }
  const hideUiPopover = () => {
    canHidePopoverUI = true
    setTimeout(
      () => {
        if (canHidePopoverUI) {
          isShowPopoverUI.value = false
        }
      },
      isMobile.value ? 5 : 1000
    )
  }
  const onMyChat = () => {
    if (route.name === 'aiCreateChat') {
      showMessage('You are already on the target page. ', 'info')
      return
    }
    hideUiPopover()
    router.push('/ai-create/chat')
  }
  const onMyAssets = () => {
    if (route.name === 'appAssets') {
      showMessage('You are already on the target page. ', 'info')
      return
    }
    hideUiPopover()
    router.push('/app-assets')
  }
  const onPurchaseHistory = () => {
    if (route.name === 'orderList') {
      showMessage('You are already on the target page. ', 'info')
      return
    }
    hideUiPopover()
    router.push('/order/list')
  }
  const onLogout = () => {
    userStore.logout()
  }
  const loopTaskStatus = () => {
    if (intervalId) {
      return
    }
    intervalId = setInterval(() => {
      let task_id = runningTaskId.value
      if (isEmpty(task_id)) {
        return
      }
      apiTaskStatus(task_id).then((data) => {
        task_id = runningTaskId.value
        if (isEmpty(task_id)) {
          return
        }
        if (data.task_status == 'failed') {
          showNotification(
            'error',
            'Generation Failed',
            `Couldn’t generate the ${
              data.task_type == 'image2video' ? 'video' : 'image'
            }. Please try with a different product image.`
          )
          // setGenerateState(0)
          refreshPoints()
          setRunningTaskId('')
        }
      })
    }, 24000)
  }
  const openSocket = () => {
    apiWsHandshake().then((data) => {
      const { send, close } = useWebSocket({
        url: `${VITE_WS_BASE_URL}/ws/listen?usid=${data.usid}&token=${getToken.value}`,
        onOpen(event) {
          let room_id = roomId.value
          if (room_id) {
            wsJoinRoom(room_id)
          }
        },
        onMessage: (message) => {
          handleSocketMessage(message)
        },
        onError: (error) => {
          console.error('WebSocket error:', error)
        },
        autoReconnect: true,
        reconnectInterval: 5000,
      })
      wsClose = close
      wsSend = send
    })
  }
  const wsJoinRoom = (room_id: string) => {
    if (typeof wsSend !== 'function') {
      setRoomId(room_id)
      return
    }
    wsSend({
      type: 'command',
      message: {
        command: 'join_room',
        room_id: room_id,
      },
    })
    setRoomId('')
    emitter.emit('joinChatRoomFinished')
  }
  const handleSocketMessage = (data: any) => {
    console.log('handle ws type', data.type)
    if (data.type === 'aitask_status') {
      handleAiTaskMessage(data)
    } else if (data.type === 'aichat') {
      handleAiChatMessage(data)
    }
  }
  const handleAiChatMessage = (data: any) => {
    console.log('handleAiChatMessage', data)
    let cur_chat = chat.value
    if (cur_chat.room_id != data.room) {
      return
    }
    let msglist = cur_chat.messages || []
    let state = 1
    if (cur_chat.state === 1) {
      for (let i = 0, len = msglist.length; i < len; i++) {
        if (msglist[i].role === 'custom-msg-warning') {
          msglist.splice(i, 1)
          break
        }
      }
    }
    let newmsg = data.message,
      contype = newmsg.content_type
    if (contype === 'message') {
      let artifact = newmsg.content.artifact
      if (artifact) {
        if (artifact.status != 0) {
          msglist.push({
            role: newmsg.role,
            content: `Errmsg: ${artifact.error_msg}. Errno: ${artifact.status}`,
          })
        } else if (artifact.images) {
          for (const res_image of artifact.images) {
            msglist.push({
              role: newmsg.role,
              prompt: res_image.prompt,
              images: res_image.images,
            })
          }
        }
      } else {
        msglist.push({
          role: newmsg.role,
          content: newmsg.content.content,
        })
      }
      state = 2
    } else if (contype === 'error') {
      console.log('chat error', newmsg)
      state = -1
    } else if (contype === 'finish') {
      console.log('chat finish', newmsg)
      state = 3
      refreshPoints()
    }
    cur_chat.messages = msglist
    cur_chat.state = state
    emitter.emit('scrollChatToBottom')
  }
  const handleAiTaskMessage = (data: any) => {
    console.log('handleAiTaskMessage', data)
    let task = data.message
    if (runningTaskId.value != task.task_id) {
      return
    }
    let is_video = runningTaskType.value === 'image2video'
    if (task.task_status == 'failed') {
      let err = task.content.error
      showNotification(
        'error',
        'Generation Failed',
        `Couldn’t generate the ${
          is_video ? 'video' : 'image'
        }. Please try with a different product image. Errmsg: ${
          err.errmsg
        }. Errno: ${err.errno}.`
      )
      setGenerateState(generatedGroupList.value.length > 0 ? 3 : 0)
      refreshPoints()
      setRunningTaskId('')
      // clearLeftMenu()
      return
    }
    if (task.task_status == 'success') {
      showNotification(
        'success',
        'Generation Completed',
        `Your masterpiece is ready! View and download now.​`
      )
      setGenerateState(3)
      refreshPoints()
      setRunningTaskId('')
      return
    }
    let running_msg = task.content.message
    if (running_msg) {
      setRunningTaskMessage(running_msg)
    }
    let results = is_video
      ? task.content.result_videos
      : task.content.result_images
    if (isEmpty(results)) {
      return
    }
    let list = []
    for (let result of results) {
      if (is_video) {
        list.push(...result.videos)
      } else {
        list.push(...result.images)
      }
    }
    for (let item of list) {
      let has_pushed = false
      for (let group of generatedGroupList.value) {
        if (group.set_idx == item.set_idx) {
          for (let item2 of group.result_list) {
            if (
              (!is_video && item2.image_id == item.image_id) ||
              (is_video && item2.video_id == item.video_id)
            ) {
              has_pushed = true
              break
            }
          }
          if (!has_pushed) {
            group.result_list.push(item)
            has_pushed = true
          }
          // 当一组设计生成完成，如果还有剩余的组还在继续生成但还没有数据返回时，需要前端自行补上没有内容的组，用于显示还有组在继续生成中。
          // 现在每组生成张数不固定是 2 了
          // if (
          //   group.result_list.length == 2 &&
          //   GenerateNumberList.value[selectedNumberIndex.value].value >
          //     generatedGroupList.value.length
          // ) {
          //   addToGeneratedGroupList({
          //     set_idx: 'app_custom_group',
          //     type: '',
          //     result_list: [],
          //   })
          // }
          break
        }
      }
      if (!has_pushed) {
        let group = {
          set_idx: item.set_idx,
          type: is_video ? 'video' : 'image',
          result_list: [item],
          task_id: task.task_id,
        }
        for (let item of generatedGroupList.value) {
          if (item.set_idx == 'app_custom_group') {
            Object.assign(item, group)
            has_pushed = true
            break
          }
        }
        if (!has_pushed) {
          addToGeneratedGroupList(group)
        }
      }
    }
    if (generateState.value === 1 && list.length > 0) {
      setGenerateState(2)
    }
  }
  const onNavigate = (item: any) => {
    let path = item.path || item
    if (path.startsWith('/')) {
      router.push(path)
    } else {
      window.open(path)
    }
    isShowMobileNav.value = false
  }
  const onUpdateTaskType = (val: string) => {
    let index = 0
    for (let i = 0, len = TaskTypeList.value.length; i < len; i++) {
      if (TaskTypeList.value[i].value === val) {
        index = i
        break
      }
    }
    router.push('/ai-create?mdx=' + index)
    isShowMobileNav.value = false
  }
  const showNotification = (
    type: NotificationType,
    title: string,
    content: string
  ) => {
    const n = notification.create({
      type: type,
      title: title,
      content: content,
      meta: formatTime(-1, 2),
      action: () =>
        h(
          NButton,
          {
            text: true,
            type: 'primary',
            style: {
              display: route.fullPath.includes('/ai-create')
                ? 'none'
                : 'inline',
            },
            onClick: () => {
              let index = 0
              for (let i = 0, len = TaskTypeList.value.length; i < len; i++) {
                if (TaskTypeList.value[i].value === selectedTaskType.value) {
                  index = i
                  break
                }
              }
              router.push('/ai-create?mdx=' + index)
              n.destroy()
            },
          },
          {
            default: () => 'Preview',
          }
        ),
      onClose: () => {},
    })
  }
  const modeSelecterSelectedRender: SelectRenderTag = ({ option }) => {
    return h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          fontSize: '1rem',
        },
      },
      [
        h(NImage, {
          src: option.icon as string,
          size: 'large',
          style: {
            marginRight: '12px',
            width: '24px',
            height: '24px',
          },
        }),
        option.label as string,
      ]
    )
  }
  const modeSelecterListRender: SelectRenderLabel = (option) => {
    return h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          fontSize: '1rem',
          width: 'auto',
          height: '38px',
          margin: '8px 16px',
          boxSizing: 'border-box',
          color:
            selectedTaskType.value === option.value ? '#2563eb' : '#303133',
        },
      },
      [
        h(NImage, {
          src: (selectedTaskType.value === option.value
            ? option.iconSelected
            : option.icon) as string,
          size: 'large',
          style: {
            marginRight: '20px',
            width: '24px',
            height: '24px',
          },
        }),
        option.label as string,
      ]
    )
  }

  // 不生效
  const modeSelecterOptionsRender = ({ option, node }) => {
    const isSelected = selectedTaskType.value === option.value
    return h(
      'div',
      {
        style: {
          // 内边距设置
          padding: '12px 20px',
          // 外边距设置
          margin: '6px 8px',
          // 圆角
          borderRadius: '8px',
          // 背景色（根据选中状态变化）
          backgroundColor: isSelected ? '#e3f2fd' : 'red',
          // 边框
          border: isSelected ? '1px solid #2196f3' : '1px solid #e0e0e0',
          // 文字颜色
          color: isSelected ? '#1976d2' : '#333',
          // 光标样式
          cursor: 'pointer',
          // 过渡效果
          transition: 'all 0.2s ease-in-out',
          // 悬停效果
          ':hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#2196f3',
          },
        },
        class: ['custom-option', { selected: isSelected }],
        onClick: () => {
          selectedTaskType.value = option.value as string
        },
      },
      [
        h(
          'span',
          {
            style: {
              fontWeight: isSelected ? '600' : '400',
              fontSize: '14px',
            },
          },
          option.label as string
        ),
        // 可以添加图标或其他元素
        isSelected &&
          h(
            'span',
            {
              style: {
                marginLeft: '8px',
                color: '#2196f3',
                fontSize: '12px',
              },
            },
            '✓'
          ),
      ].filter(Boolean) // 过滤掉 false 值
    )
  }
  const googleLoginCallback = (response) => {
    fastLogin({ credential: response.credential })
  }
</script>

<template>
  <header class="header" :class="{ dark: theme === 'dark' }">
    <div v-if="isChrome && isReady && !isLoggedIn" style="display: none">
      <GoogleLogin :callback="googleLoginCallback" prompt autoLogin />
    </div>
    <div class="container">
      <router-link to="/" class="desktop-logo">
        <img :src="imgLogo" />
      </router-link>
      <nav class="desktop-nav-tabs">
        <div
          v-if="'removebg.pro' === VITE_APP_NAME"
          class="nav-tab"
          :class="{
            active: route.name === 'home',
          }"
          @click="onNavigate('/home')"
        >
          Home
        </div>
        <!-- :render-options="modeSelecterOptionsRender" 不生效 -->
        <n-popselect
          :consistent-menu-width="false"
          v-model:value="selectedTaskType"
          :options="TaskTypeList"
          :render-label="modeSelecterListRender"
          :render-tag="modeSelecterSelectedRender"
          :on-update:value="onUpdateTaskType"
          trigger="hover"
        >
          <div class="nav-tab" :class="{ active: route.name === 'aiCreate' }">
            AI Product Photography
            <span>⌄</span>
          </div>
        </n-popselect>
        <div
          v-for="(item, index) in navItems"
          :key="index"
          class="nav-tab"
          :class="{
            active: curNavIndex === index,
          }"
          @click="onNavigate(item)"
        >
          {{ $t(item.name) }}
        </div>
      </nav>
      <div class="right-wrapper">
        <!-- <div class="language-selector">
          <select v-model="currentLocale" @change="changeLanguage">
            <option v-for="locale in supportedLocales" :key="locale" :value="locale">
              {{ $t(`language.${locale}`) }}
            </option>
          </select>
        </div> -->
        <!-- <div v-if="isLoggedIn" class="credits-bar">
          <img
            class="img-credits"
            src="@/assets/icon/ic-credits.png"
            @click="onCredits"
          />
          <span>{{ userStore.points }}</span>
        </div> -->
        <n-popover v-if="isLoggedIn" trigger="manual" :show="isShowPopoverUI">
          <template #trigger>
            <img
              class="img-avatar"
              :src="userStore.avatar"
              @mouseenter="showUiPopover"
              @mouseleave="hideUiPopover"
            />
          </template>
          <div
            class="ui-popover"
            @mouseenter="showUiPopover"
            @mouseleave="hideUiPopover"
          >
            <div class="basic-info">
              <img :src="userStore.avatar" />
              <span class="username">{{
                userStore.nickname || userStore.name
              }}</span>
              <span class="email">{{ userStore.email }}</span>
            </div>
            <div class="row-cell row-credits">
              <img src="@/assets/icon/ic-crown.png" />
              <span>{{ userStore.points }} credits</span>
              <div class="btn-primary btn-upgrade" @click="onCredits">
                Upgrade
              </div>
            </div>
            <div class="row-cell" @click="onMyChat">
              <img src="@/assets/icon/ic-mychat.png" />
              <span>My Chat</span>
            </div>
            <!-- <div class="row-cell" @click="onMyAssets">
              <img src="@/assets/icon/ic-file.png" />
              <span>My Assets</span>
            </div> -->
            <div class="row-cell" @click="onPurchaseHistory">
              <img src="@/assets/icon/ic-purchase-history.png" />
              <span>Purchase History</span>
            </div>
            <div class="row-cell" style="margin-bottom: 1rem" @click="onLogout">
              <img src="@/assets/icon/ic-logout.png" />
              <span>Logout</span>
            </div>
          </div>
        </n-popover>
        <div v-else class="btn-primary" @click="onLogin">
          <!-- <img src="@/assets/icon/ic-login.png" /> -->
          <span>Log in / Sign up</span>
        </div>
        <!-- <button class="theme-toggle" @click="toggleTheme">
          {{ theme === 'light' ? '🌙' : '☀️' }}
        </button> -->
      </div>
      <router-link to="/" class="mobile-logo">
        <img :src="iconLogo" />
      </router-link>
      <n-popover trigger="manual" :show="isShowMobileNav">
        <template #trigger>
          <img
            class="mobile-menu"
            src="@/assets/icon/ic-list.png"
            @click="isShowMobileNav = !isShowMobileNav"
          />
        </template>
        <div class="mobile-nav-tabs">
          <div
            v-if="'removebg.pro' === VITE_APP_NAME"
            class="nav-tab"
            :class="{
              active: route.name === 'home',
            }"
            @click="onNavigate('/home')"
          >
            Home
          </div>
          <n-popselect
            v-model:value="selectedTaskType"
            :options="TaskTypeList"
            :render-label="modeSelecterListRender"
            :render-tag="modeSelecterSelectedRender"
            :on-update:value="onUpdateTaskType"
            trigger="hover"
          >
            <div class="nav-tab" :class="{ active: route.name === 'aiCreate' }">
              AI Product Photography
              <span>⌄</span>
            </div>
          </n-popselect>
          <div
            v-for="(item, index) in navItems"
            :key="index"
            class="nav-tab"
            :class="{
              active: curNavIndex === index,
            }"
            @click="onNavigate(item)"
          >
            {{ $t(item.name) }}
          </div>
        </div>
      </n-popover>
    </div>
    <LoginDialog ref="loginDialog" />
  </header>
</template>

<style lang="scss" scoped>
  :deep(.n-popselect-dropdown .n-popselect-option) {
    padding: 12px 16px !important;
    margin: 40px 0 !important;
    background-color: aqua;
  }

  .header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    z-index: 2;
    background-color: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .dark {
      background-color: var(--color-primary);
      color: white;
    }
  }

  .container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100vw;
    height: 3.4rem;
  }

  .desktop-logo {
    display: flex;
    position: absolute;
    left: 1rem;

    img {
      height: 1.6rem;
      vertical-align: middle;
    }
  }

  .desktop-nav-tabs {
    display: flex;
    gap: 1.8rem;
  }

  .nav-tab {
    display: block;
    text-decoration: none;
    color: var(--color-text-primary);
    padding: 0.3rem;
    margin: 0.4rem;
    transition: color 0.1s ease-in-out;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;

    span {
      position: relative;
      top: -0.3rem;
      margin-left: 0.3rem;
    }
  }

  .nav-tab:hover {
    color: var(--color-primary);
  }

  /* 激活状态样式，只要当前路由​​包含​​导航链接的路由路径，就会应用这个类 */
  .nav-tab.active {
    color: var(--color-primary);
    border-bottom: 2px solid var(--color-primary);
  }

  /* 精确匹配样式（可选），只有当前路由​​完全等于​​导航链接的路由路径时，才会应用这个类 */
  .nav-tab.exact-active {
    background-color: rgba(00, 158, 255, 0);
  }

  .nav-tab.right {
    margin: 0 0.4rem 0 8rem;
  }

  .right-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 1.2rem;
    gap: 0.8rem;
  }

  .language-selector {
    display: inline-block;

    select {
      height: 2rem;
      padding: 8px 30px 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      background-color: white;
      width: 120px;
      font-size: 0.9rem;
      cursor: pointer;
    }
  }

  .language-selector::after {
    content: '▼';
    font-size: 12px;
    color: var(--color-text-primary);
    position: absolute;
    left: 100px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .theme-toggle {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
  }

  .credits-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-right: 0.4vw;
    gap: 0.4rem;

    img {
      height: 1.4rem;
      width: 1.4rem;
      padding: 0.4rem;
      border-radius: 50%;
      background-color: var(--bg-color-base);
    }

    img:hover {
      background-color: #e4e7ed;
      cursor: pointer;
    }

    span {
      font-size: 1rem;
      font-weight: 300;
      color: var(--color-text-primary);
    }
  }

  .img-avatar {
    height: 2.4rem;
    width: 2.4rem;
    border-radius: 50%;
    cursor: pointer;
    background-image: url('../../assets/icon/ic-avatar.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  .ui-popover {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: min(24rem, 80vw);

    .basic-info {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      border-top-left-radius: 0.8rem;
      border-top-right-radius: 0.8rem;
      background-color: #f8f8f8;

      img {
        height: 3.6rem;
        width: 3.6rem;
        margin-top: 1.6rem;
        border-radius: 50%;
        background-image: url('../../assets/icon/ic-avatar.png');
        background-repeat: no-repeat;
        background-position: center;
        background-size: cover;
      }

      .username {
        margin-top: 0.4rem;
        font-size: 1.2rem;
        color: var(--color-text-primary);
      }

      .email {
        margin-top: 0.2rem;
        margin-bottom: 1rem;
        font-size: 1rem;
        font-weight: 400;
        color: var(--color-text-regular);
      }
    }

    .row-cell {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      height: 4rem;
      margin: 0 0.4rem;
      border-radius: 0.8rem;
      transition: background-color 0.3s ease;
      cursor: pointer;

      img {
        width: 1.4rem;
        height: auto;
        margin-left: 1rem;
        margin-right: 1rem;
      }

      span {
        font-size: 1.2rem;
        color: var(--color-text-primary);
      }
    }

    @media (hover: hover) {
      .row-cell:hover {
        background-color: #f8f8f8;
      }
    }

    .row-credits {
      position: relative;
      border-top: 1px #cdcdcd dotted;
      background-color: #fafafa;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      margin-bottom: 1rem;
      cursor: default;

      span {
        font-weight: 300;
      }

      .btn-upgrade {
        position: absolute;
        right: 1rem;
      }
    }
  }

  .btn-primary {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 0.2rem 1.2rem;
    border-radius: 1.4rem;
    background: linear-gradient(
      -45deg,
      #315dff 0%,
      #3682fc 30%,
      #3dacf9 60%,
      #40bcf8 100%
    );
    box-shadow: 1px 1px 8px 2px #cdcdcd;
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: 1;
    cursor: pointer;
    font-size: 1rem;
    color: #fff;
  }

  @media (hover: hover) {
    .btn-primary:hover {
      transform: scale(1.04);
      opacity: 0.9;
    }
  }

  .mobile-logo {
    position: absolute;
    display: none;
    width: 1.4rem;
    height: 1.4rem;
    left: 0.6rem;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      border-radius: 4px;
    }
  }

  .mobile-menu {
    position: absolute;
    display: none;
    width: 1.8rem;
    height: 1.8rem;
    left: 2.6rem;
    cursor: pointer;
  }

  .mobile-nav-tabs {
    display: none;
    flex-direction: column;
    align-items: flex-start;
    padding: 0.2rem;
  }

  @media (max-width: 768px) {
    .desktop-logo {
      display: none;
    }

    .desktop-nav-tabs {
      display: none;
    }

    .mobile-logo {
      display: block;
    }

    .mobile-menu {
      display: block;
    }

    .mobile-nav-tabs {
      display: flex;
    }

    .right-wrapper {
      right: 0.6rem;
    }
  }
</style>
